'use client';

import { useTheme } from '@emotion/react';
import { DateTime } from 'luxon';
import { CSSProperties, useEffect, useState } from 'react';
import { Link } from '../../styled/Link';
import { Container } from '../Container';
import masterCardImg from '../../../public/img/mastercard.png';
import mirImg from '../../../public/img/mir.png';
import visaImg from '../../../public/img/visa.png';
import LockSVG from '../../../public/img/lock.svg';
import LogoFooterSVG from '../../../public/img/logo-footer.svg';
import BlogLogoSVG from '../../../public/img/logo-blog.svg';
import SKLogoSVG from '../../../public/img/logo-sk-uchastnik.svg';
import { FooterList, FooterTitle } from './styled';
import { Icon } from '../../icon/icon';
import {
  DEVICE_SCREENS,
  DEVICE_SCREENS_MAX_STRING,
  DEVICE_SCREENS_MIN_STRING,
} from '../../../lib/consts';
import { City, Page, Social } from '../../../models';
import { Contact } from '../../../models/contact';
import { BLOG_DOMAIN } from '../../../lib/api/apiUrl';
import { UniqueContent } from '../../../models/page';
import styles from './Footer.module.scss';

const SITE_SECTIONS = {
  company: 'company',
  clients: 'for_clients',
  help: 'help',
};

const CONCACT_TYPES = {
  email: 'email',
  telephone: 'телефон',
  openingHours: 'график',
  name: 'название',
  address: 'адрес',
};

function constructPageLinkByUniqueContent(
  uniqueContent: Extract<
    keyof typeof UniqueContent,
    'places' | 'collections' | 'site_map'
  >,
  currentCity: City
) {
  switch (uniqueContent) {
    case UniqueContent.places:
      return `/${currentCity.url}/place-catalog/`;
    case UniqueContent.collections:
      return `/${currentCity.url}/compilations/`;
    case UniqueContent.site_map:
      return `/${currentCity.url}/sitemap/`;
    default:
      return '';
  }
}

export function Footer({
  style,
  pageList,
  socials,
  socialsLinks,
  contactList,
  currentCity,
  className,
}: {
  style?: CSSProperties;
  pageList: { results: Page[] };
  socials: { results: Social[] };
  socialsLinks: { results: Social[] };
  contactList: { results: Contact[] };
  currentCity: City;
  className?: string;
}) {
  const theme = useTheme();
  const currentYear = DateTime.now().year;
  const [isMobileMode, setIsMobileMode] = useState(false);
  useEffect(() => {
    setIsMobileMode(window.innerWidth < DEVICE_SCREENS.sm);
  }, []);
  const companyPages = pageList?.results.filter(
    (page) => page.site_section === SITE_SECTIONS.company
  );
  const clientsPages = pageList?.results.filter(
    (page) => page.site_section === SITE_SECTIONS.clients
  );
  const helpPages = pageList?.results.filter(
    (page) => page.site_section === SITE_SECTIONS.help
  );

  return (
    <footer
      className={className}
      style={{
        ...style,
        padding: '40px 0',
        backgroundColor: theme.colors.bgFooter,
        color: theme.colors.white,
        fontWeight: 500,
      }}
    >
      <Container className={styles.container}>
        <div className={styles.content}>
          <div className={styles.logo}>
            <div className={styles.bankInfo}>
              <ul className={styles.bankInfoLogos}>
                <li>
                  <img
                    src={masterCardImg.src}
                    loading='lazy'
                    title='mastercard'
                    alt='mastercard'
                  />
                </li>
                <li>
                  <img
                    src={mirImg.src}
                    style={{ width: '90%' }}
                    loading='lazy'
                    title='mir'
                    alt='mir'
                  />
                </li>
                <li style={{ width: 38 }}>
                  <img
                    width='38px'
                    height='17.2px'
                    src={visaImg.src}
                    loading='lazy'
                    title='visa'
                    alt='visa'
                  />
                </li>
              </ul>
              <ul
                // bank_info__features
                className={styles.bankInfoFeatures}
              >
                <li>
                  <LockSVG
                    css={{
                      left: '-30px',
                    }}
                    loading='lazy'
                    title='lock icon'
                    alt='lock icon'
                  />
                  <p>
                    Безопасные платежи картой любого банка. Защита персональных
                    данных.
                  </p>
                </li>
              </ul>
            </div>
            <div
              // logo logo_footer
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <LogoFooterSVG
                style={{
                  width: '120px',
                  height: '20px',
                  fill: theme.colors.white,
                }}
              />
            </div>
            <div>
              <SKLogoSVG
                style={{
                  width: '120px',
                  height: '40px',
                  marginTop: 12,
                }}
              />
            </div>
            <span
              style={{
                fontSize: '10px',
              }}
            >
              © 2015-{currentYear} Gorbilet.com
            </span>
          </div>
          <div
            // footer__divider footer__divider_vertical
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              flex: '0 0 auto',
              width: '2px',
              margin: '0 90px',
              padding: 0,
              order: 1,
            }}
          />
          <div
            className={styles.dividerHorizontal}
            // footer__divider footer__divider_horizontal
          />
          {!!companyPages.length && (
            <div
              // footer__company
              className={styles.company}
            >
              <FooterTitle>Компания</FooterTitle>
              <FooterList>
                {companyPages.map((page) =>
                  page.link === null ? (
                    <li key={`CompanyPageLi__${page.id}`}>
                      <Link
                        {...(page.open_in_new_window
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : {})}
                        key={`CompanyPageLink__${page.id}`}
                        href={constructPageLinkByUniqueContent(
                          page.unique_content,
                          currentCity
                        )}
                      >
                        {page.title}
                      </Link>
                    </li>
                  ) : (
                    <li key={`CompanyPageLi__${page.id}`}>
                      <a
                        {...(page.open_in_new_window
                          ? { target: '_blank', rel: 'noopener noreferrer' }
                          : {})}
                        key={`CompanyPageLink__${page.id}`}
                        href={page.link}
                      >
                        {page.title}
                      </a>
                    </li>
                  )
                )}
              </FooterList>
            </div>
          )}
          {!!helpPages.length && (
            <div
              // footer_help
              className={styles.help}
            >
              <FooterTitle>Помощь</FooterTitle>
              <FooterList>
                {helpPages.map((page) => (
                  <li key={`HelpPageLi__${page.id}`}>
                    <Link
                      key={`HelpPageLink__${page.id}`}
                      href={
                        page.link === null
                          ? constructPageLinkByUniqueContent(
                              page.unique_content,
                              currentCity
                            )
                          : page.link
                      }
                    >
                      {page.title}
                    </Link>
                  </li>
                ))}
              </FooterList>
              <div
              // footer__help-social
              >
                {!!socials?.results.length && !isMobileMode && (
                  <div
                    // social-question
                    style={{
                      minWidth: '184px',
                    }}
                  >
                    <div>
                      <div
                        // social-question__head
                        css={styles.socialQuestionHead}
                      >
                        Задать вопрос
                      </div>
                      <div
                        // social-question__body
                        className={styles.socialQuestionBody}
                      >
                        {socials.results.map((social) => (
                          <Link isBlank key={social.id} href={social.url}>
                            <Icon iconId={social.type} />
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {!!clientsPages.length && (
            <div
              // footer_clients
              className={styles.clients}
            >
              <FooterTitle>Клиентам</FooterTitle>
              <FooterList>
                {clientsPages.map((page) => (
                  <li key={`ClientsPageLi__${page.id}`}>
                    <Link
                      key={`ClientsPageLink__${page.id}`}
                      href={
                        page.link === null
                          ? constructPageLinkByUniqueContent(
                              page.unique_content,
                              currentCity
                            )
                          : page.link
                      }
                    >
                      {page.title}
                    </Link>
                  </li>
                ))}
              </FooterList>
            </div>
          )}
          <div
            itemScope
            itemType='https://schema.org/Organization'
            // footer_contacts
            className={styles.contacts}
          >
            <meta
              itemProp='url'
              content={`https://${process.env.DEPLOY_DOMAIN}`}
            />
            <meta
              itemProp='logo'
              content={`https://${process.env.DEPLOY_DOMAIN}/apple-touch-icon-114x114.png/`}
            />
            <meta itemProp='sameAs' content='https://vk.me/gorbilet' />
            <meta
              itemProp='sameAs'
              content='https://t.me/Gorbilet_otdel_zaboty_bot'
            />
            <FooterTitle> Контакты</FooterTitle>
            <FooterList>
              {contactList?.results.map((contact) => (
                <li key={`ClientsPageLi__${contact.id}`}>
                  {contact.type === CONCACT_TYPES.email && (
                    // eslint-disable-next-line jsx-a11y/control-has-associated-label
                    <a
                      href={`mailto:${contact.information}`}
                      itemProp='email'
                      dangerouslySetInnerHTML={{ __html: contact.information }}
                    />
                  )}
                  {contact.type === CONCACT_TYPES.telephone && (
                    <span
                      itemScope
                      itemType='https://schema.org/ContactPoint'
                      itemProp='contactPoint'
                    >
                      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                      <a
                        href={`tel:${contact.information}`}
                        dangerouslySetInnerHTML={{
                          __html: contact.information,
                        }}
                      />
                      <meta
                        itemProp='telephone'
                        content={contact.information}
                      />
                      <meta itemProp='contactType' content='customer service' />
                    </span>
                  )}
                  {contact.type === CONCACT_TYPES.openingHours && (
                    <span
                      itemScope
                      itemType='http://schema.org/EntertainmentBusiness'
                    >
                      <span style={{ display: 'none' }} itemProp='name'>
                        {/* eslint-disable-next-line react/no-unescaped-entities */}
                        ООО "Горбилет"
                      </span>
                      <span style={{ display: 'none' }} itemProp='address'>
                        наб. канала Грибоедова, 5
                      </span>
                      <span style={{ display: 'none' }} itemProp='telephone'>
                        +7 (812) 603 46 33
                      </span>
                      <time
                        dateTime='Mo-Su 10:00-22:00'
                        itemProp='openingHours'
                        dangerouslySetInnerHTML={{
                          __html: contact.information,
                        }}
                      />
                    </span>
                  )}
                  {contact.type === CONCACT_TYPES.name && (
                    <div
                      itemProp='name'
                      dangerouslySetInnerHTML={{
                        __html: contact.information,
                      }}
                    />
                  )}
                  {contact.type === CONCACT_TYPES.address && (
                    <div
                      itemProp='address'
                      itemScope
                      itemType='http://schema.org/PostalAddress'
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: contact.information,
                        }}
                      />
                      <meta itemProp='name' content={contact.information} />
                    </div>
                  )}
                </li>
              ))}
              {!!socials?.results.length && isMobileMode && (
                <div
                  // social-question
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <div>
                    <div
                      // social-question__head
                      className={styles.socialQuestionHead2}
                    >
                      Задать вопрос
                    </div>
                    <div
                      // social-question__body
                      className={styles.socialQuestionBody}
                    >
                      {socials.results.map((social) => (
                        <a key={`SocialAnchor__${social.id}`} href={social.url}>
                          <Icon iconId={social.type} />
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </FooterList>
            <div
              // footer__social
              css={{
                marginTop: '12px',
                [DEVICE_SCREENS_MAX_STRING.sm]: {
                  display: 'none',
                  order: 0,
                },
              }}
            >
              <FooterTitle
                style={{
                  display: 'none',
                  marginBottom: '12px',
                }}
              >
                Мы в соцсетях
              </FooterTitle>
              <div
                // social-icons
                className={styles.socialIcons}
              >
                <div
                  // body
                  css={{
                    svg: {
                      width: '22px',
                      height: '22px',
                    },
                    display: 'flex',
                    flexWrap: 'wrap',
                    img: {
                      maxWidth: 22,
                    },
                    'a:not(:last-child)': {
                      marginRight: '12px',
                      marginBottom: '6px',
                    },
                  }}
                >
                  {socialsLinks.results.map((social) => (
                    <a
                      target='_blank'
                      rel='noopener noreferrer'
                      key={social.id}
                      href={social.url}
                      aria-label={social.title}
                    >
                      <Icon iconId={social.type} />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div
            css={{
              fontFamily: 'Golos',
              margin: '0 auto',
              paddingRight: theme.constants.padding,
              paddingLeft: theme.constants.padding,
              flex: '1 0 0',
              order: 5,
              [DEVICE_SCREENS_MAX_STRING.lg]: {
                width: '100%',
              },
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                order: 0,
                marginBottom: 30,
              },
            }}
          >
            <a
              href={[BLOG_DOMAIN, 'blog', ''].join('/')}
              target='_blank'
              rel='noreferrer'
              aria-label='ссылка на сайт Горбилет блог'
            >
              <BlogLogoSVG />
            </a>

            <p>Актуальные материалы о культурной жизни города</p>
          </div>
          <div
            // footer__social
            css={{
              marginTop: '12px',
              display: 'none',
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                order: 0,
                marginBottom: 30,
              },
            }}
          >
            <FooterTitle
              style={{
                fontFamily: 'Golos',
                display: 'none',
                marginBottom: '12px',
              }}
            >
              Мы в соцсетях
            </FooterTitle>
            <div
              // social-icons
              className={styles.socialIcons}
            >
              <div
                // body
                css={{
                  svg: {
                    width: '22px',
                    height: '22px',
                  },
                  img: {
                    maxWidth: 22,
                  },
                  display: 'flex',
                  flexWrap: 'wrap',
                  'a:not(:last-child)': {
                    marginRight: '12px',
                    marginBottom: '6px',
                  },
                }}
              >
                {socialsLinks.results.map((social) => (
                  <a
                    target='_blank'
                    rel='noopener noreferrer'
                    key={`SocialLinkIcon__${social.id}`}
                    href={social.url}
                    aria-label={social.title}
                  >
                    <Icon iconId={social.type} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
}
